import { Link } from 'react-router-dom';
import { ArrowRight, Search, UserCheck, FileText, Send, CalendarCheck, Rocket } from 'lucide-react';
import PageHeader from '../components/layout/PageHeader';

const HEADER_BG = 'https://images.unsplash.com/photo-1764181725776-c40871005b41?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBvZmZpY2UlMjBza3lsaW5lJTIwY2l0eSUyMGJ1c2luZXNzfGVufDB8fHx8MTc3MjQ0MDI1Nnww&ixlib=rb-4.1.0&q=85';

const steps = [
  { icon: Search, num: '01', title: 'Requirement Analysis', desc: 'We begin by thoroughly understanding your technical requirements, team dynamics, project timelines, and organizational culture to define the ideal candidate profile.' },
  { icon: UserCheck, num: '02', title: 'Candidate Screening', desc: 'Our recruiters leverage proprietary databases, professional networks, and targeted sourcing strategies to identify and pre-screen qualified candidates.' },
  { icon: FileText, num: '03', title: 'Resume Structuring', desc: 'Selected candidate profiles are professionally reformatted and optimized to meet enterprise submission standards, highlighting relevant experience and skills.' },
  { icon: Send, num: '04', title: 'Client Submission', desc: 'Shortlisted candidates are submitted with comprehensive profiles, including skill assessments, availability, and compensation expectations.' },
  { icon: CalendarCheck, num: '05', title: 'Interview Coordination', desc: 'We manage the entire interview lifecycle including scheduling, preparation briefings, feedback collection, and offer negotiation support.' },
  { icon: Rocket, num: '06', title: 'Deployment', desc: 'Post-selection, we handle onboarding logistics, documentation, compliance verification, and ongoing performance check-ins to ensure a smooth transition.' },
];

const OurProcess = () => {
  return (
    <div data-testid="our-process-page">
      <PageHeader caption="How We Work" title="Our Process" subtitle="A structured, transparent approach to delivering exceptional talent." bgImage={HEADER_BG} />

      <section className="bg-white sol-section">
        <div className="sol-container">
          <div className="text-center mb-16">
            <p className="sol-caption mb-4">Step by Step</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>From Requirement to Deployment</h2>
            <p className="text-[#525252] mt-4 max-w-2xl mx-auto">Every engagement follows our proven six-step process, ensuring consistency, quality, and transparency at every stage.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {steps.map((s) => (
              <div key={s.num} data-testid={`process-step-${s.num}`} className="sol-service-card group">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-12 h-12 flex items-center justify-center border border-[#E5E5E5] group-hover:border-black group-hover:bg-black transition-all duration-300">
                    <s.icon className="w-5 h-5 text-black group-hover:text-white transition-colors duration-300" strokeWidth={1.5} />
                  </div>
                  <span className="text-3xl font-bold text-[#E5E5E5] group-hover:text-black transition-colors duration-300" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.num}</span>
                </div>
                <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.title}</h3>
                <p className="text-sm text-[#525252] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F5F5F5] sol-section">
        <div className="sol-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="sol-caption mb-4">Our Commitment</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Quality at Every Step</h2>
              <p className="text-[#525252] leading-relaxed mb-6">Our process is designed to minimize time-to-hire while maximizing candidate quality. Every step includes built-in quality checkpoints and client communication touchpoints.</p>
              <ul className="space-y-4">
                {['Dedicated account manager assigned to every engagement', 'Real-time status updates and candidate pipeline visibility', 'Average 48-72 hour turnaround for initial candidate submissions', 'Post-placement support and performance monitoring', 'Replacement guarantee for all placements'].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-[#333333]"><div className="w-1.5 h-1.5 bg-black rounded-full mt-2 shrink-0" />{item}</li>
                ))}
              </ul>
            </div>
            <div className="bg-white border border-[#E5E5E5] p-10 md:p-12">
              <div className="space-y-8">
                {[{ metric: '48-72 hrs', label: 'Average Time to First Submission' }, { metric: '95%', label: 'Interview-to-Placement Ratio' }, { metric: '< 5%', label: 'Annual Attrition Rate' }, { metric: '100%', label: 'Compliance Adherence' }].map((m) => (
                  <div key={m.label} className="border-b border-[#E5E5E5] pb-6 last:border-0 last:pb-0">
                    <div className="text-2xl font-bold text-black mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{m.metric}</div>
                    <div className="text-sm text-[#525252]">{m.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="sol-cta-strip">
        <div className="sol-container text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Experience Our Process</h2>
          <p className="text-white/60 mb-8 max-w-lg mx-auto">See how our proven process can help you build the team you need.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/hire-talent" data-testid="process-cta-hire" className="sol-btn-inverse text-sm">Hire Talent <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/contact" className="sol-btn-inverse text-sm">Contact Us <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurProcess;
